import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { venueUrl } from "../../utils/venueUrl";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Building2, MapPin, Eye, Facebook, Instagram } from "lucide-react";
import { BilingualInput, BilingualTextarea } from "../../components/BilingualField";
import OwnerDraggableMap from "./OwnerDraggableMap";
import VenueTagChipGroups from "../../components/VenueTagChipGroups";

export default function OwnerVenuePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [venue, setVenue] = useState(null);
  const [venueTypes, setVenueTypes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const localizedName = (item) => {
    if (!item) return "";
    return (lang === "el" && item.name_el) || item.name || "";
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [venueRes, typesRes, tagsRes] = await Promise.all([
          axios.get(`${API}/owner/venue`),
          axios.get(`${API}/taxonomy/venue-types`),
          axios.get(`${API}/owner/venue/tags`).catch(() => ({ data: { tag_ids: [] } })),
        ]);
        setVenue(venueRes.data);
        setVenueTypes(typesRes.data || []);
        setTagIds(tagsRes.data?.tag_ids || []);
      } catch (error) {
        console.error("Error fetching venue:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Load areas for the venue's district. Areas are scoped per-district.
  useEffect(() => {
    const districtId = venue?.district_id || venue?.city_id;
    if (!districtId) { setAreas([]); return; }
    let cancelled = false;
    axios.get(`${API}/taxonomy/areas`, { params: { district_id: districtId } })
      .then((r) => { if (!cancelled) setAreas(r.data || []); })
      .catch(() => { if (!cancelled) setAreas([]); });
    return () => { cancelled = true; };
  }, [venue?.district_id, venue?.city_id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/owner/venue`, {
        description_short: venue.description_short,
        description_short_el: venue.description_short_el,
        description_long: venue.description_long,
        description_long_el: venue.description_long_el,
        phone: venue.phone,
        email: venue.email,
        website: venue.website,
        facebook_url: venue.facebook_url,
        instagram_url: venue.instagram_url,
        reservation_policy: venue.reservation_policy,
        reservation_policy_el: venue.reservation_policy_el,
        booking_mode: venue.booking_mode,
        latitude: venue.latitude ? parseFloat(venue.latitude) : undefined,
        longitude: venue.longitude ? parseFloat(venue.longitude) : undefined,
        address: venue.address,
        address_el: venue.address_el,
        formatted_address: venue.formatted_address,
        formatted_address_el: venue.formatted_address_el,
        area_id: venue.area_id || "",
        specialty_ids: Array.isArray(venue.specialty_ids) ? venue.specialty_ids : [],
        tag_ids: tagIds,
      });
      toast.success(t("owner_venue:save_success"));
    } catch (error) {
      toast.error(t("owner_venue:save_error"));
    } finally {
      setSaving(false);
    }
  };

  // Tag chips toggle helper — selections persist via the
  // /owner/venue/tags endpoint as a list of tag_ids.
  const toggleTag = (id) => {
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (loading) {
    return <div className="animate-pulse bg-muted h-96 rounded-xl" />;
  }

  if (!venue) {
    return (
      <div className="text-center py-12 bg-card rounded-xl border border-border">
        <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t("owner_venue:no_venue")}</p>
      </div>
    );
  }

  return (
    <div data-testid="owner-venue-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("owner_venue:title")}</h1>
        <div className="flex items-center gap-2">
          <Link to="/owner/preview" data-testid="owner-venue-internal-preview">
            <Button variant="ghost" size="sm" className="gap-1.5">
              <Eye className="h-4 w-4" />
              {t("owner_venue:preview", { defaultValue: "Preview" })}
            </Button>
          </Link>
          {venue.slug && (
            <a
              href={venueUrl(venue)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="owner-venue-view-as-customer"
            >
              <Button variant="outline" size="sm" className="gap-1.5">
                <Eye className="h-4 w-4" />
                {t("owner_venue:view_as_customer", { defaultValue: "View as Customer" })}
              </Button>
            </a>
          )}
        </div>
      </div>

      <form onSubmit={handleSave} className="bg-card rounded-xl border border-border p-6 space-y-6">
        <div>
          <Label>{t("owner_venue:name")}</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div className="relative">
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-medium bg-muted px-1 pointer-events-none">EN</span>
              <Input value={venue.name || ""} disabled className="bg-muted pr-8" data-testid="owner-venue-name" />
            </div>
            <div className="relative">
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-medium bg-muted px-1 pointer-events-none">EL</span>
              <Input value={venue.name_el || ""} disabled className="bg-muted pr-8" data-testid="owner-venue-name-el" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t("owner_venue:name_help")}</p>
        </div>

        <BilingualTextarea
          label={t("owner_venue:short_description")}
          value={venue.description_short || ""}
          valueEl={venue.description_short_el || ""}
          onChange={(e) => setVenue({ ...venue, description_short: e.target.value })}
          onChangeEl={(e) => setVenue({ ...venue, description_short_el: e.target.value })}
          rows={2}
          data-testid="owner-venue-desc-short"
        />

        <BilingualTextarea
          label={t("owner_venue:full_description")}
          value={venue.description_long || ""}
          valueEl={venue.description_long_el || ""}
          onChange={(e) => setVenue({ ...venue, description_long: e.target.value })}
          onChangeEl={(e) => setVenue({ ...venue, description_long_el: e.target.value })}
          rows={5}
          data-testid="owner-venue-desc-long"
        />

        {/* Tag chip groups (Cuisine / Music / Mood / Amenity / Feature)
            — driven by the venue type so only relevant groups appear. */}
        {(() => {
          const selectedTypeSlug = venueTypes.find((vt) => vt.id === venue.venue_type_id)?.slug || "";
          return (
            <div data-testid="owner-venue-tag-groups">
              <VenueTagChipGroups
                venueTypeSlug={selectedTypeSlug}
                tagIds={tagIds}
                onTagToggle={toggleTag}
              />
            </div>
          );
        })()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone">{t("owner_venue:phone")}</Label>
            <Input
              id="phone"
              value={venue.phone || ""}
              onChange={(e) => setVenue({ ...venue, phone: e.target.value })}
              className="mt-1"
              data-testid="owner-venue-phone"
            />
          </div>
          <div>
            <Label htmlFor="email">{t("owner_venue:email")}</Label>
            <Input
              id="email"
              type="email"
              value={venue.email || ""}
              onChange={(e) => setVenue({ ...venue, email: e.target.value })}
              className="mt-1"
              data-testid="owner-venue-email"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="website">{t("owner_venue:website")}</Label>
          <Input
            id="website"
            value={venue.website || ""}
            onChange={(e) => setVenue({ ...venue, website: e.target.value })}
            className="mt-1"
            data-testid="owner-venue-website"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="facebook_url" className="flex items-center gap-1.5">
              <Facebook className="h-4 w-4" /> {t("owner_venue:facebook_url", { defaultValue: "Facebook URL" })}
            </Label>
            <Input
              id="facebook_url"
              type="url"
              value={venue.facebook_url || ""}
              onChange={(e) => setVenue({ ...venue, facebook_url: e.target.value })}
              className="mt-1"
              placeholder="https://www.facebook.com/yourpage"
              data-testid="owner-venue-facebook"
            />
          </div>
          <div>
            <Label htmlFor="instagram_url" className="flex items-center gap-1.5">
              <Instagram className="h-4 w-4" /> {t("owner_venue:instagram_url", { defaultValue: "Instagram URL" })}
            </Label>
            <Input
              id="instagram_url"
              type="url"
              value={venue.instagram_url || ""}
              onChange={(e) => setVenue({ ...venue, instagram_url: e.target.value })}
              className="mt-1"
              placeholder="https://www.instagram.com/yourpage"
              data-testid="owner-venue-instagram"
            />
          </div>
        </div>

        {/* Reservation Policy (bilingual) */}
        <div className="border-t border-border pt-6 mt-6">
          <h3 className="text-lg font-medium text-foreground mb-4">{t("owner_venue:reservation_policy")}</h3>
          <BilingualTextarea
            label=""
            value={venue.reservation_policy || ""}
            valueEl={venue.reservation_policy_el || ""}
            onChange={(e) => setVenue({ ...venue, reservation_policy: e.target.value })}
            onChangeEl={(e) => setVenue({ ...venue, reservation_policy_el: e.target.value })}
            rows={3}
            data-testid="owner-venue-policy"
          />
        </div>

        {/* Booking Mode */}
        <div>
          <Label>{t("owner_venue:booking_mode")}</Label>
          <Select value={venue.booking_mode || "instant_confirm"} onValueChange={(v) => setVenue({ ...venue, booking_mode: v })}>
            <SelectTrigger className="mt-1" data-testid="owner-venue-booking-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant_confirm">{t("owner_venue:booking_mode_instant")}</SelectItem>
              <SelectItem value="manual_confirm">{t("owner_venue:booking_mode_request")}</SelectItem>
              <SelectItem value="request_only">{t("owner_venue:booking_mode_request")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Location with Map */}
        <div className="border-t border-border pt-6 mt-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" /> {t("owner_venue:location")}
          </h3>
          <div className="space-y-4">
            <BilingualInput
              label={t("owner_venue:street_address")}
              value={venue.address || ""}
              valueEl={venue.address_el || ""}
              onChange={(e) => setVenue({ ...venue, address: e.target.value })}
              onChangeEl={(e) => setVenue({ ...venue, address_el: e.target.value })}
              data-testid="owner-venue-address"
            />
            <BilingualInput
              label={t("owner_venue:formatted_address")}
              value={venue.formatted_address || ""}
              valueEl={venue.formatted_address_el || ""}
              onChange={(e) => setVenue({ ...venue, formatted_address: e.target.value })}
              onChangeEl={(e) => setVenue({ ...venue, formatted_address_el: e.target.value })}
              placeholder={t("owner_venue:formatted_address_hint")}
              data-testid="owner-venue-formatted-address"
            />
            <div>
              <Label>{t("admin_venue_edit:field_area", { defaultValue: "Area" })}</Label>
              <Select
                value={venue.area_id || "__none__"}
                onValueChange={(v) => setVenue({ ...venue, area_id: v === "__none__" ? "" : v })}
                disabled={!(venue.district_id || venue.city_id)}
              >
                <SelectTrigger className="mt-1" data-testid="owner-venue-area">
                  <SelectValue placeholder={
                    !(venue.district_id || venue.city_id)
                      ? t("admin_venue_edit:area_pick_city_first", { defaultValue: "Pick a district first" })
                      : areas.length === 0
                        ? t("admin_venue_edit:area_none_for_city", { defaultValue: "No areas defined for this district" })
                        : t("admin_venue_edit:select_area", { defaultValue: "Select area (optional)" })
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("admin_venue_edit:no_area", { defaultValue: "— No area —" })}</SelectItem>
                  {areas.map((a) => (
                    <SelectItem key={a.area_id || a.id} value={a.area_id || a.id}>{localizedName(a)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("owner_venue:latitude")}</Label>
                <Input type="number" step="any" value={venue.latitude || ""} onChange={(e) => setVenue({ ...venue, latitude: e.target.value })} className="mt-1" data-testid="owner-venue-lat" />
              </div>
              <div>
                <Label>{t("owner_venue:longitude")}</Label>
                <Input type="number" step="any" value={venue.longitude || ""} onChange={(e) => setVenue({ ...venue, longitude: e.target.value })} className="mt-1" data-testid="owner-venue-lng" />
              </div>
            </div>
            <OwnerDraggableMap
              lat={parseFloat(venue.latitude) || 35.1856}
              lng={parseFloat(venue.longitude) || 33.3823}
              onMove={(lat, lng) => setVenue(v => ({ ...v, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }))}
            />
            <p className="text-xs text-muted-foreground">{t("owner_venue:map_hint")}</p>
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="owner-venue-save">
          {saving ? t("owner_venue:saving") : t("owner_venue:save")}
        </Button>
      </form>
    </div>
  );
}
